import React, { Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services/Config.js";
import { customEvent } from "src/utils/gtag.js";
import { lazyWithRetry } from "src/utils/lazyWithRetry.js";
import {
  CALENDAR_LIMIT_SHOWN_KEY,
  calendarLimitNotShownRecently,
  isOwnCreatorTimeline,
  isSuppressedRoute,
  stampNow,
} from "src/utils/interstitial.js";

// Deferred so the payment surface and plan table stay out of the entry bundle
// for the visitors — anonymous, paid, under the cap — who never see this.
// lazyWithRetry, not React.lazy: a stale chunk after a deploy would otherwise
// reach the app-level error boundary and replace the whole page.
const CalendarLimitInterstitialDialog = lazyWithRetry(() =>
  import("src/components/upgrades/consumer/CalendarLimitInterstitialDialog.js")
);

/**
 * Interstitial nudging signed-in free users at the calendar limit toward Stanza+.
 */
export default function CalendarLimitInterstitial() {
  // Mounted at app root, so tolerate rendering outside AuthProvider.
  const { user, isLoading, refetchAuth } = useContext(AuthContext) ?? {};
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Set once the dialog actually mounts (see handleShown), not on open-intent,
  // so a transient open closed by post-sign-in navigation before the lazy chunk
  // renders doesn't burn the one-per-load slot. Guards reopening after dismissal.
  const shownRef = useRef(false);
  // Path the dialog opened on, so the dismiss-on-navigation effect can tell a
  // real route change from the commit that opened it.
  const openedAtPathRef = useRef(null);

  // The creator's own timeline is a management surface, not a consumer browse
  // surface — never interrupt it with the consumer calendar-limit upsell.
  const onSuppressedRoute =
    isSuppressedRoute(location) || isOwnCreatorTimeline(location, user);

  useEffect(() => {
    if (isLoading || open || onSuppressedRoute || shownRef.current) return;
    // needsConsumerCalendarUpgrade = free user at/over (calendars + groups)
    // limit; the canonical gate the backend CALENDAR_LIMIT path enforces.
    const eligible =
      !!user && user.needsConsumerCalendarUpgrade && calendarLimitNotShownRecently();
    if (!eligible) return;
    openedAtPathRef.current = location.pathname;
    setOpen(true);
  }, [user, isLoading, onSuppressedRoute, open, location.pathname]);

  // Fired by the dialog once it is actually mounted, so a chunk that never
  // loads (or an open interrupted by navigation before it renders) neither
  // reports an impression, spends the fatigue window, nor burns the slot.
  const handleShown = useCallback(() => {
    shownRef.current = true;
    stampNow(CALENDAR_LIMIT_SHOWN_KEY);
    customEvent({
      name: "calendar_limit_interstitial_shown",
      category: "Upgrade",
      label: "Calendar Limit Interstitial Shown",
      data: { user_id: user?._id, user_email: user?.email },
    });
  }, [user?._id, user?.email]);

  // Dismiss when the user routes away, so the modal never sits on top of the
  // destination. Comparing against the path it opened on matters: both effects
  // run in the same commit when a navigation is what made the user eligible,
  // and an unconditional close there would burn the fatigue window unseen.
  // A suppressed location closes it even on the same path (`?writeReview`).
  useEffect(() => {
    if (!openedAtPathRef.current) return;
    if (!onSuppressedRoute && openedAtPathRef.current === location.pathname) return;
    setOpen(false);
  }, [location.pathname, onSuppressedRoute]);

  // Sign-out ends the session: reset the per-load guard so the next sign-in
  // re-evaluates (the globally-mounted component never unmounts to do this on
  // its own). Cross-session re-shows are still capped by the fatigue window.
  useEffect(() => {
    if (user) return;
    shownRef.current = false;
    openedAtPathRef.current = null;
    setOpen(false);
  }, [user]);

  // Unmount in the render that reaches a suppressed route, before that page's
  // CardElement mounts: the app-wide Elements allows only one card Element.
  if (!open || !user || onSuppressedRoute) return null;

  const handleClose = () => {
    setOpen(false);
    customEvent({
      name: "calendar_limit_interstitial_dismissed",
      category: "Upgrade",
      label: "Calendar Limit Interstitial Dismissed",
      data: { user_id: user._id, user_email: user.email },
    });
  };
  const handlePaid = () => {
    setOpen(false);
    refetchAuth?.();
    navigate(getPageRoute("upgrade", "PAYMENT_STANZA_PLUS_CONFIRMATION"));
  };

  return (
    <Suspense fallback={null}>
      <CalendarLimitInterstitialDialog
        onShown={handleShown}
        onClose={handleClose}
        onPaid={handlePaid}
      />
    </Suspense>
  );
}
